import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, Observable, Subscription } from 'rxjs';
import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import { SocialGroupService } from 'src/app/interfaces/social-groups.service';
import { TableCellOnActionEvent } from 'src/app/interfaces/tables.interfaces';
import {
  Communities,
  Community,
  CommunityInviteOrDeclineRequest,
  PaginatablePendingUserInvolvementsInCommunities,
  RequestCounters,
} from 'src/app/swagger-api';
import { withReplaceBadRequest } from '../../services/api-error';
import { InvolvementInSocialGroup } from '../projects/projects.service';
import { SocialInitiativeCommunityEntities } from '../shared/social-initiative/social-initiative-community-entities.abstract';

@Injectable({ providedIn: 'root' })
export class CommunitiesService extends SocialInitiativeCommunityEntities implements SocialGroupService<Community> {
  public communitiesChanges$: BehaviorSubject<Communities> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });

  public communitiesCounterChanges$: BehaviorSubject<RequestCounters> = new BehaviorSubject({
    verified_count: 0,
    requests_sent: 0,
    requests_received: 0,
  });

  private pendingRequestsChanges$: BehaviorSubject<InvolvementInSocialGroup> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });

  private pendingInvitesChanges$: BehaviorSubject<InvolvementInSocialGroup> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });

  public get socialGroupChanges(): Observable<Communities> {
    return this.communitiesChanges$.asObservable();
  }

  public get pendingRequestsChanges(): Observable<InvolvementInSocialGroup> {
    return this.pendingRequestsChanges$.asObservable();
  }

  public get pendingInvitesChanges(): Observable<InvolvementInSocialGroup> {
    return this.pendingInvitesChanges$.asObservable();
  }

  public get counterChanges(): Observable<RequestCounters> {
    return this.communitiesCounterChanges$.asObservable();
  }

  public get communitiesCounterChanges(): Observable<RequestCounters> {
    return this.communitiesCounterChanges$.asObservable();
  }

  public fetchRequestCounters(): void {
    const subscription: Subscription = this.apiService
      .restSocialCommunitiesInvolvementCountersGet()
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: RequestCounters) => {
        this.communitiesCounterChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchUserPersonalRequests(offset?: number, limit?: number, sorting?: string): void {
    this.apiService
      .restSocialCommunitiesSentRequestsIndex(offset, limit, sorting, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((requests: PaginatablePendingUserInvolvementsInCommunities) => {
        this.userPersonalCommunityRequests = requests.items;

        this.pendingRequestsChanges$.next(requests);
      });
  }

  public fetchUserPersonalInvites(offset?: number, limit?: number, sorting?: string): void {
    this.apiService
      .restSocialCommunitiesGotInvitationsIndex(offset, limit, sorting, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((requests: PaginatablePendingUserInvolvementsInCommunities) => {
        this.userPersonalInvites = requests.items;

        this.pendingInvitesChanges$.next(requests);
      });
  }

  public onRemoveInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserPersonalCommunityRequests(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserPersonalInvites(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserPersonalInvites(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public removeInvite(event: TableCellOnActionEvent): Observable<void> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public rejectRequestOrInvite(event: TableCellOnActionEvent): Observable<void> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public confirmRequestOrInvite(event: TableCellOnActionEvent): Observable<void> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(withReplaceBadRequest(null)) as Observable<void>;
  }

  protected fetchUserEntities(offset?: number, limit?: number): void {
    this.apiService
      .restSocialCommunitiesIndex(offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((communities: Communities) => this.communitiesChanges$.next(communities));
  }

  /**
   *
   * @param userId {string} - user id
   * @param offset {number} - offset of the first item in the collection to return
   * @param limit {number} - maximum number of entries to return
   */
  protected fetchUserSpecificEntities(userId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialUserCommunitiesIndex(userId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((communities: Communities) => this.communitiesChanges$.next(communities));
  }

  protected completeSubject(): void {
    if (this.communitiesChanges$) {
      this.communitiesChanges$.complete();
    }
  }
}
